import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Anchor, Wrench, Cog, Ship, CheckCircle, Thermometer, Gauge, Shield, ArrowRight } from "lucide-react";

const Products = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Products & Services</h1>
            <p className="text-xl text-blue-100">
              Comprehensive engineering solutions for marine and industrial operations
            </p>
          </div>
        </div>
      </section>

      {/* Products & Services Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="marine-hose" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-6 mb-8">
              <TabsTrigger value="marine-hose" className="text-xs sm:text-sm px-2 py-2">Marine Hose</TabsTrigger>
              <TabsTrigger value="industrial-hose" className="text-xs sm:text-sm px-2 py-2">Industrial Hose</TabsTrigger>
              <TabsTrigger value="pig-launcher" className="text-xs sm:text-sm px-2 py-2">Pig Launcher</TabsTrigger>
              <TabsTrigger value="valves" className="text-xs sm:text-sm px-2 py-2">Valves</TabsTrigger>
              <TabsTrigger value="process-skids" className="text-xs sm:text-sm px-2 py-2">Process Skids</TabsTrigger>
              <TabsTrigger value="ship-brokering" className="text-sm">Ship Brokering</TabsTrigger>
            </TabsList>

            {/* Floating Marine Hose */}
            <TabsContent value="marine-hose">
              <div className="space-y-8">
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Anchor className="h-8 w-8 text-yellow-600" />
                      </div>
                      <div>
                        <CardTitle className="text-3xl">Floating Marine Hose Systems</CardTitle>
                        <CardDescription className="text-lg">
                          Engineered for offshore oil transfer operations
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <img 
                          src="/images/marine_hose_1.webp" 
                          alt="Floating Marine Hose" 
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Product Overview</h3>
                        <p className="text-gray-700 mb-6">
                          Our floating and submarine marine hoses are designed for offshore oil transfer operations including SPM, SBM, FPSO, FSO, STS, and CBM systems.
                        </p>
                        
                        <h4 className="font-semibold mb-3">Types Available:</h4>
                        <ul className="space-y-2 mb-6">
                          {[
                            "Floating Marine Hose",
                            "Submarine Hose", 
                            "Offloading Hose",
                            "Tanker Rail Hose",
                            "Hose Repair Kits & Accessories"
                          ].map((type, index) => (
                            <li key={index} className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span>{type}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Key Features</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Compliant with GMPHOM 2009",
                              "High tensile reinforcement",
                              "Abrasion & UV resistant",
                              "Long service life",
                              "Custom length and flange options"
                            ].map((feature, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-blue-500" />
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Applications</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Oil terminals",
                              "Offshore fields",
                              "Marine loading arms",
                              "Floating storage units",
                              "FPSO/FSO operations"
                            ].map((app, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Anchor className="h-4 w-4 text-yellow-600" />
                                <span className="text-sm">{app}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Flexible Industrial Hose */}
            <TabsContent value="industrial-hose">
              <div className="space-y-8">
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <Wrench className="h-8 w-8 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-3xl">Flexible Industrial Hose</CardTitle>
                        <CardDescription className="text-lg">
                          Complete range for refineries and industrial facilities
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <img 
                          src="/images/industrial_hose_1.webp" 
                          alt="Industrial Hose" 
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Product Range</h3>
                        <div className="grid grid-cols-1 gap-4">
                          {[
                            "Oil & Fuel Transfer Hose",
                            "Chemical Resistant Hose",
                            "High-Pressure Hydraulic Hose",
                            "Steam & Air Hose",
                            "Composite Hose",
                            "Industrial Rubber Hose"
                          ].map((product, index) => (
                            <Badge key={index} variant="outline" className="justify-start p-3">
                              {product}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center space-x-2">
                            <Thermometer className="h-5 w-5" />
                            <span>Specifications</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Temperature range: -40°C to 120°C",
                              "Multi-layer reinforcement",
                              "Oil & chemical compatible linings",
                              "Various pressure ratings",
                              "Custom assemblies available"
                            ].map((spec, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Gauge className="h-4 w-4 text-green-500" />
                                <span className="text-sm">{spec}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Industries Served</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Refineries",
                              "Petrochemical",
                              "Manufacturing",
                              "Logistics terminals",
                              "Power plants"
                            ].map((industry, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Wrench className="h-4 w-4 text-green-600" />
                                <span className="text-sm">{industry}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Pig Launcher & Receiver Systems */}
            <TabsContent value="pig-launcher">
              <div className="space-y-8">
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <Cog className="h-8 w-8 text-gray-600" />
                      </div>
                      <div>
                        <CardTitle className="text-3xl">Pig Launcher & Receiver Systems</CardTitle>
                        <CardDescription className="text-lg">
                          Custom-designed systems for pipeline maintenance
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <div className="space-y-4">
                          <img 
                            src="/images/pig_launcher_1.webp" 
                            alt="Pig Launcher System" 
                            className="w-full h-48 object-cover rounded-lg shadow-md"
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <img 
                              src="/images/pig_launcher_additional_1.png" 
                              alt="Pig Launcher & Receiver" 
                              className="w-full h-32 object-cover rounded-lg shadow-md"
                            />
                            <img 
                              src="/images/pig_launcher_additional_2.jpeg" 
                              alt="Pipeline Pig Launcher" 
                              className="w-full h-32 object-cover rounded-lg shadow-md"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Engineering Overview</h3>
                        <p className="text-gray-700 mb-6">
                          We supply and design custom pig launcher & receiver systems used for pipeline cleaning, maintenance, and commissioning.
                        </p>
                        
                        <h4 className="font-semibold mb-3">Components:</h4>
                        <ul className="space-y-2">
                          {[
                            "Pig Launcher",
                            "Pig Receiver",
                            "Pig Signallers",
                            "Quick Opening Closure (QOC)",
                            "Scrapper Pigs, Brush Pigs, Foam Pigs",
                            "Valves & pipeline accessories"
                          ].map((component, index) => (
                            <li key={index} className="flex items-center space-x-2">
                              <Cog className="h-4 w-4 text-gray-600" />
                              <span className="text-sm">{component}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center space-x-2">
                            <Shield className="h-5 w-5" />
                            <span>Advantages</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Built to ASME standards",
                              "High-pressure capability",
                              "Minimal downtime",
                              "Custom sizes for pipeline diameter",
                              "Complete engineering support"
                            ].map((advantage, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span className="text-sm">{advantage}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Applications</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Pipeline cleaning",
                              "Pipeline commissioning",
                              "Maintenance operations",
                              "Product separation",
                              "Inspection runs"
                            ].map((app, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Cog className="h-4 w-4 text-gray-600" />
                                <span className="text-sm">{app}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Ship Brokering & Marine Agency */}
            <TabsContent value="ship-brokering">
              <div className="space-y-8">
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <Ship className="h-8 w-8 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-3xl">Ship Brokering & Marine Agency</CardTitle>
                        <CardDescription className="text-lg">
                          Comprehensive marine logistics and vessel services
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <img 
                          src="/images/ship_brokering_1.png" 
                          alt="Offshore Support Vessel" 
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Services Provided</h3>
                        <div className="space-y-3">
                          {[
                            "Vessel Chartering & Sourcing",
                            "Offshore Support Vessel (OSV) Arrangement",
                            "Tug & Barge Charter",
                            "Bunker Supply Coordination",
                            "Ship-to-Ship (STS) Support",
                            "Marine Logistics & Documentation"
                          ].map((service, index) => (
                            <Card key={index} className="p-3 bg-blue-50 border-blue-200">
                              <div className="flex items-center space-x-2">
                                <Ship className="h-4 w-4 text-blue-600" />
                                <span className="font-medium text-sm">{service}</span>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Industries Served</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Oil & Gas",
                              "Marine Construction",
                              "Port Logistics",
                              "Cargo Transport",
                              "Offshore Operations"
                            ].map((industry, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Ship className="h-4 w-4 text-blue-600" />
                                <span className="text-sm">{industry}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Our Expertise</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {[
                              "Regional vessel network",
                              "Marine documentation",
                              "Port agency services",
                              "Logistics coordination",
                              "Emergency response"
                            ].map((expertise, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span className="text-sm">{expertise}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Valve Products */}
            <TabsContent value="valves">
              <div className="space-y-8">
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                        <Cog className="h-8 w-8 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-3xl">Valve Products</CardTitle>
                        <CardDescription className="text-lg">
                          Comprehensive valve solutions for oil & gas operations
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Valve Images Gallery */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                      <img 
                        src="/images/valve_products_1.jpeg" 
                        alt="Industrial Valves Collection" 
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-md"
                      />
                      <img 
                        src="/images/valve_products_2.jpeg" 
                        alt="Oil & Gas Valves"
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-md"
                      />
                      <img 
                        src="/images/valve_products_3.webp" 
                        alt="API 6D Industrial Valves" 
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-md"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Product Overview</h3>
                        <p className="text-gray-700 mb-6">
                          G5 Petroleum supplies a comprehensive range of valves engineered for oil & gas, petrochemical, offshore operations, and industrial plants. All valves are manufactured to international standards and suitable for high-pressure and corrosive environments.
                        </p>
                        
                        <h4 className="font-semibold mb-3">Valve Types:</h4>
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            "Ball Valve",
                            "Gate Valve",
                            "Globe Valve",
                            "Check Valve",
                            "Plug Valve",
                            "Butterfly Valve",
                            "Cryogenic Valve",
                            "High-Pressure API Valves",
                            "Floating & Trunnion Mounted Ball Valves"
                          ].map((valve, index) => (
                            <Badge key={index} variant="outline" className="justify-start p-2">
                              {valve}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="space-y-6">
                          <Card className="bg-gray-50">
                            <CardHeader>
                              <CardTitle className="text-lg">Standards Compliance</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <ul className="space-y-2">
                                {[
                                  "API 6D",
                                  "API 608",
                                  "ANSI/ASME",
                                  "BS/EN",
                                  "ISO"
                                ].map((standard, index) => (
                                  <li key={index} className="flex items-center space-x-2">
                                    <Shield className="h-4 w-4 text-purple-500" />
                                    <span className="text-sm">{standard}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                          
                          <Card className="bg-gray-50">
                            <CardHeader>
                              <CardTitle className="text-lg">Material Options</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <ul className="space-y-2">
                                {[
                                  "Carbon Steel",
                                  "Stainless Steel",
                                  "Duplex / Super Duplex",
                                  "Alloy Steel"
                                ].map((material, index) => (
                                  <li key={index} className="flex items-center space-x-2">
                                    <Cog className="h-4 w-4 text-purple-600" />
                                    <span className="text-sm">{material}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-8">
                      <Card className="bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">Industries Served</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {[
                              "Oil & Gas",
                              "Petrochemical",
                              "Pipeline Transmission",
                              "Refining",
                              "Power Generation",
                              "Marine Terminals"
                            ].map((industry, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <Cog className="h-4 w-4 text-purple-600" />
                                <span className="text-sm">{industry}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Process Skid Packages */}
            <TabsContent value="process-skids">
              <div className="space-y-8">
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Wrench className="h-8 w-8 text-indigo-600" />
                      </div>
                      <div>
                        <CardTitle className="text-3xl">Process Skid Packages</CardTitle>
                        <CardDescription className="text-lg">
                          Modular process systems for fast deployment
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Process Skid Images Gallery */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                      <img 
                        src="/images/process_skid_1.webp" 
                        alt="Modular Process Skid" 
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-md"
                      />
                      <img 
                        src="/images/process_skid_2.jpeg"
                        alt="Process Skid Package"
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-md"
                      />
                      <img 
                        src="/images/process_skid_3.jpeg" 
                        alt="Metering Skid System" 
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-md"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                        <h3 className="text-xl font-semibold mb-4">Engineering Overview</h3>
                        <p className="text-gray-700 mb-6">
                          G5 Petroleum designs, fabricates, and assembles modular process skid packages for oil & gas and industrial process applications. Our skid systems are engineered for fast installation, reduced site work, and minimum downtime.
                        </p>
                        
                        <h4 className="font-semibold mb-3">Available Skid Packages:</h4>
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            "Metering Skid",
                            "Filtration Skid",
                            "Pump Skid",
                            "Chemical Injection Skid",
                            "Gas Dehydration Skid",
                            "Pigging Skid Module",
                            "Fuel Gas Conditioning Skid",
                            "Sampling Skid",
                            "Hydraulic Power Unit (HPU)"
                          ].map((skid, index) => (
                            <Badge key={index} variant="outline" className="justify-start p-2">
                              {skid}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="space-y-6">
                          <Card className="bg-gray-50">
                            <CardHeader>
                              <CardTitle className="text-lg">Key Advantages</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <ul className="space-y-2">
                                {[
                                  "Fully integrated modular design",
                                  "ASME/API compliant fabrication",
                                  "Factory-tested & certified",
                                  "Quick deployment",
                                  "Reduced on-site construction",
                                  "Custom-built to project data sheets"
                                ].map((advantage, index) => (
                                  <li key={index} className="flex items-center space-x-2">
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                    <span className="text-sm">{advantage}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                          
                          <Card className="bg-gray-50">
                            <CardHeader>
                              <CardTitle className="text-lg">Design Capability</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <ul className="space-y-2">
                                {[
                                  "P&ID Development",
                                  "Engineering Documentation",
                                  "Material Selection",
                                  "Instrumentation & Control Integration",
                                  "Testing & Certification"
                                ].map((capability, index) => (
                                  <li key={index} className="flex items-center space-x-2">
                                    <Wrench className="h-4 w-4 text-indigo-600" />
                                    <span className="text-sm">{capability}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Discuss Your Requirements?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Contact our engineering team for customized solutions and competitive quotations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4">
              Request Quotation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4">
              Contact Engineering Team
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Products;